import { FORM_VALIDATION_MSG, MAX_LENGTH_CONSTRAINTS } from './constants';
import { booleanOverlap, booleanContains, booleanWithin } from '@turf/turf';
import { EQUIPMENT_FLOW } from '../EditCustomer/constants';
import { USER_ROLE } from '../../../../appConstants';
import { weekdays, weekdaysShortName } from '../../Settings/Optimization/AnchorPoint/addUpdateAnchorPoint/DayOfWeekStatus/Constants';

const validateLocationName = (locationName) => {
  return locationName?.length > MAX_LENGTH_CONSTRAINTS.LOCATION_NAME
    ? FORM_VALIDATION_MSG.MAX_LENGTH_EXCEEDED
    : null;
};

const getFieldError = ({ fieldName, value, isMatchedLocation }) => {
  if (fieldName === 'locationName') {
    if (isMatchedLocation) {
      return FORM_VALIDATION_MSG.MATCHED_LOCATION;
    }
    return value ? validateLocationName(value) : FORM_VALIDATION_MSG.REQUIRED;
  } else {
    return null;
  }
};

const getErrorInfo = (errorInfo, isMatchedLocation) => {
  const newErrorInfo = {};
  Object.keys(errorInfo).forEach((fieldName) => {
    newErrorInfo[fieldName] = getFieldError({
      fieldName,
      value: errorInfo[fieldName],
      isMatchedLocation
    });
  });
  return newErrorInfo;
};

const getLoaderMessage = (isLocationUpdateInProgress, isOperationDelayed) => {
  let message = 'Fetching Existing Locations';
  if (isLocationUpdateInProgress) {
    message = isOperationDelayed
      ? 'Location update is taking more time'
      : 'Location update is in progress';
  }
  return message;
};

const isSameLocation = (previousLocation, currentLocation) => {
  return previousLocation?.trim()?.toLowerCase() === currentLocation?.trim()?.toLowerCase();
};

const checkIfOverlappingRegions = (geoDataList, newBoundary) => {
  return geoDataList.some(
    (eachRegion) =>
      eachRegion.id !== newBoundary.id &&
      (
        booleanOverlap(eachRegion, newBoundary) ||
        booleanContains(eachRegion, newBoundary) ||
        booleanWithin(eachRegion, newBoundary)
      )
  );
};

const isLocationMappingEditable = (userProfile) => {
  const { isAdmin, roles } = userProfile || {};
  return isAdmin || roles.includes(USER_ROLE.MANAGER);
}

const getLocationCardDetails = (location) => {
  if (location.fixedEquipmentSize) {
    return [
      {
        label: 'Fixed Equip Size',
        size: location.fixedEquipmentSize
      }
    ];
  }
  const labelsList = [];
  if (location.maxEquipmentSize) {
    labelsList.push({
      label: 'Max Equip Size',
      size: location.maxEquipmentSize
    });
  }
  if (location.minEquipmentSize) {
    labelsList.push({
      label: 'Min Equip Size',
      size: location.minEquipmentSize
    });
  }
  return labelsList.length === 0
    ? [
        {
          label: 'Equip Size',
          size: 'NA'
        }
      ]
    : labelsList;
};

const isEquipmentUpdateValid = ({ equipmentType, [equipmentType]: { minEquipmentSize, maxEquipmentSize } = {} }) => {
  return !(
    equipmentType === EQUIPMENT_FLOW.FLEXIBLE &&
    minEquipmentSize &&
    maxEquipmentSize &&
    (maxEquipmentSize <= minEquipmentSize)
  );
};

const getDeliveryDays = (dowDelWindow) => {
  const dowList = dowDelWindow?.map((day) => day.toLowerCase()) || [];
  return weekdays.map((day) => ({
    name: day,
    value: dowList.includes(day.toLowerCase())
  }))
}

const getDowLocationCardDetails = (dowDelWindow) => {
  return [
    {
      label: 'Delivery Days',
      value:
        dowDelWindow?.map((day) => weekdaysShortName[day.toLowerCase()])?.join(' | ') ||
        'N/A'
    }
  ];
};

const getExistingFeaturesGeoJSON = (formattedLocationMappingData) => {
  return formattedLocationMappingData.map((feature) => {
    return {
      id: feature.locationMappingId,
      type: "Feature",
      properties: {},
      geometry: {
        type: "Polygon",
        coordinates: [feature.boundary],
      },
    };
  });
};

export {
  getFieldError,
  getErrorInfo,
  getLoaderMessage,
  isSameLocation,
  checkIfOverlappingRegions,
  isLocationMappingEditable,
  getLocationCardDetails,
  isEquipmentUpdateValid,
  getDeliveryDays,
  getDowLocationCardDetails,
  getExistingFeaturesGeoJSON
};
